import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { saveAs } from "file-saver";
import "./SvuotamentoCampane.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import DatePickerModal from "../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../components/Lf23Loader";

const SvuotamentoCampane = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [dati, setDati] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(20);
    const [showInfo, setShowInfo] = useState(false);

    // UI State
    const [viewMode, setViewMode] = useState("table");
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // Helper function to get default date range (from yesterday to today)
    const getDefaultDateRange = () => {
        const today = new Date();
        const yesterday = new Date(today);
        // yesterday.setDate(today.getDate() - 1);

        const formatDate = (date) => {
            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        };

        return {
            from: formatDate(yesterday),
            to: formatDate(today)
        };
    };

    const defaultDates = getDefaultDateRange();

    // Filtri
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [comune, setComune] = useState("");
    const [targa, setTarga] = useState("");
    const [gestitoFilter, setGestitoFilter] = useState("tutti");

    const [sortConfig, setSortConfig] = useState({ key: 'data', direction: 'desc' });

    const handleSort = (key) => {
        setSortConfig(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    // Stats globali
    const [stats, setStats] = useState({
        totalRecords: 0,
        totalKg: 0,
        avgKg: 0,
        recordsGestiti: 0
    });

    const fetchStats = async () => {
        try {
            const res = await axios.get(`${API_URL}/interacta/svuotamento-campane`, {
                params: {
                    page: 1,
                    limit: 999999,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    comune,
                    targa,
                    gestito: gestitoFilter
                }
            });

            const allData = res.data.data || [];
            const totalKg = allData.reduce((sum, row) => sum + (row.carico_tot_kg || 0), 0);
            const avgKg = allData.length > 0 ? totalKg / allData.length : 0;
            const recordsGestiti = allData.filter(row => row.gestito).length;

            setStats({
                totalRecords: res.data.total || 0,
                totalKg,
                avgKg,
                recordsGestiti
            });
        } catch (err) {
            console.error("Errore nel caricamento statistiche:", err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/interacta/svuotamento-campane`, {
                params: { page, limit, fromDate: fromDate || undefined, toDate: toDate || undefined, comune, targa, gestito: gestitoFilter, sortBy: sortConfig.key || undefined, sortDir: sortConfig.direction || undefined }
            });

            setDati(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error("Errore nella fetch:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchStats();
        // eslint-disable-next-line
    }, [page, limit, sortConfig]);

    useEffect(() => {
        setPage(1);
    }, [sortConfig]);

    const totalPages = Math.ceil(total / limit);

    const handleReset = () => {
        const defaultDates = getDefaultDateRange();
        setFromDate(defaultDates.from);
        setToDate(defaultDates.to);
        setComune("");
        setTarga("");
        setGestitoFilter("tutti");
        setPage(1);
        setSearchTerm("");
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            alert("La data iniziale non può essere successiva alla data finale.");
            return;
        }
        setPage(1);
        fetchData();
        fetchStats();
    };

    const handleDownloadExcel = async () => {
        const params = new URLSearchParams({ fromDate, toDate, comune, targa, gestito: gestitoFilter });

        try {
            const res = await fetch(`${API_URL}/interacta/export?${params.toString()}`);
            const blob = await res.blob();
            saveAs(blob, "report_svuotamento_campane.xlsx");
        } catch (err) {
            console.error("Errore durante il download Excel:", err);
        }
    };

    const filteredDati = dati.filter(row => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            row.comune?.toLowerCase().includes(search) ||
            row.targa?.toLowerCase().includes(search) ||
            row.ods?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="campane-container-modern">
            <header className="page-header-campane">
                <div className="header-content-campane">
                    <button className="back-button-campane" onClick={() => navigate('/produttivita')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title-campane">
                        <div className="header-title-row-campane">
                            <h2>Svuotamento Campane</h2>
                            <button
                                className="info-button-campane"
                                onClick={() => setShowInfo(!showInfo)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text-campane">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle-campane">Monitoraggio carichi campane e gestione attività</p>
                    </div>
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay-campane" onClick={() => setShowInfo(false)}>
                    <div className="modal-container-campane" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-campane">
                            <div className="modal-icon-campane">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                                    <path d="M10 8h6"></path>
                                    <path d="M10 12h6"></path>
                                    <path d="M10 16h4"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close-campane" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body-campane">
                            <div className="modal-section-campane">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa sezione mostra i <strong>carichi totali (in kg)</strong> raccolti dai mezzi
                                    per ogni attività di svuotamento delle campane.
                                </p>
                                <p>
                                    Puoi <strong>filtrare</strong> i dati per periodo, comune, targa e stato di gestione,
                                    e <strong>marcare come gestiti</strong> i record una volta verificati.
                                </p>
                            </div>
                            <div className="modal-section-campane">
                                <h4>Funzionalità disponibili</h4>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>Visualizza i dati in <strong>modalità tabella</strong> o <strong>griglia</strong></li>
                                    <li>Filtra per periodo (7, 30, 60 giorni), comune, targa e stato gestione</li>
                                    <li>Marca/smarca record come gestiti con checkbox</li>
                                    <li>Aggiungi note personalizzate per ogni record</li>
                                    <li>Esporta i dati filtrati in formato Excel</li>
                                    <li>Ricerca rapida per comune, targa o ODS</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area-campane">
                {/* Top Controls */}
                <div className="top-controls-campane">
                    <div className="controls-left-campane">
                        <button
                            className={`toggle-filters-btn-campane ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                            </svg>
                            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                        </button>

                        <div className="search-bar-campane">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca comune, targa, ODS..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right-campane">
                        <div className="view-toggle-compact">
                            <button
                                className={`view-btn-compact ${viewMode === "table" ? "active" : ""}`}
                                onClick={() => setViewMode("table")}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9h18M3 15h18M9 3v18"></path>
                                </svg>
                                Tabella
                            </button>
                            <button
                                className={`view-btn-compact ${viewMode === "cards" ? "active" : ""}`}
                                onClick={() => setViewMode("cards")}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>

                        <button className="export-btn-campane" onClick={handleDownloadExcel}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Esporta Excel
                        </button>

                        <div className="results-badge-campane">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                            <span>{filteredDati.length} risultati</span>
                        </div>
                    </div>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel-campane">
                        <form className="filters-form-campane" onSubmit={handleSubmit}>
                            <div className="filters-grid-campane">
                                <div className="filter-item-campane">
                                    <label>Dal</label>
                                    <DatePickerModal
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        max={toDate || undefined}
                                        label="Dal"
                                    />
                                </div>

                                <div className="filter-item-campane">
                                    <label>Al</label>
                                    <DatePickerModal
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        min={fromDate || undefined}
                                        label="Al"
                                    />
                                </div>

                                <div className="filter-item-campane">
                                    <label>Comune</label>
                                    <input
                                        type="text"
                                        placeholder="Comune..."
                                        value={comune}
                                        onChange={(e) => setComune(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item-campane">
                                    <label>Targa</label>
                                    <input
                                        type="text"
                                        placeholder="Targa..."
                                        value={targa}
                                        onChange={(e) => setTarga(e.target.value.trim())}
                                    />
                                </div>

                                <div className="filter-item-campane">
                                    <label>Stato Gestione</label>
                                    <select value={gestitoFilter} onChange={(e) => setGestitoFilter(e.target.value)}>
                                        <option value="tutti">Tutti</option>
                                        <option value="si">Gestiti</option>
                                        <option value="no">Non Gestiti</option>
                                    </select>
                                </div>

                                <div className="filter-item-campane filter-actions-campane">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons-campane">
                                        <button type="submit" className="btn-apply-campane">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Applica
                                        </button>
                                        <button type="button" className="btn-reset-campane" onClick={handleReset}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* KPI Stats Cards */}
                <div className="stats-grid-campane">
                    <div className="stat-card-campane stat-primary-campane">
                        <div className="stat-icon-campane">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 7h-9"></path>
                                <path d="M14 17H5"></path>
                                <circle cx="17" cy="17" r="3"></circle>
                                <circle cx="7" cy="7" r="3"></circle>
                            </svg>
                        </div>
                        <div className="stat-content-campane">
                            <p className="stat-label-campane">Totale Record</p>
                            <p className="stat-value-campane">{stats.totalRecords}</p>
                        </div>
                    </div>

                    <div className="stat-card-campane stat-success-campane">
                        <div className="stat-icon-campane">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-campane">
                            <p className="stat-label-campane">Carico Totale</p>
                            <p className="stat-value-campane">{stats.totalKg.toFixed(0)} kg</p>
                        </div>
                    </div>

                    <div className="stat-card-campane stat-warning-campane">
                        <div className="stat-icon-campane">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="20" x2="12" y2="10"></line>
                                <line x1="18" y1="20" x2="18" y2="4"></line>
                                <line x1="6" y1="20" x2="6" y2="16"></line>
                            </svg>
                        </div>
                        <div className="stat-content-campane">
                            <p className="stat-label-campane">Media per Attività</p>
                            <p className="stat-value-campane">{stats.avgKg.toFixed(0)} kg</p>
                        </div>
                    </div>

                    <div className="stat-card-campane stat-info-campane">
                        <div className="stat-icon-campane">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-campane">
                            <p className="stat-label-campane">Record Gestiti</p>
                            <p className="stat-value-campane">{stats.recordsGestiti}</p>
                        </div>
                    </div>
                </div>

                {/* Data View */}
                {loading ? (
                    <Lf23Loader />
                ) : filteredDati.length === 0 ? (
                    <div className="empty-state-campane">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <h3>Nessun dato disponibile</h3>
                        <p>Non sono stati trovati record con i filtri selezionati</p>
                    </div>
                ) : viewMode === "table" ? (
                    <>
                    <div className="fullscreen-btn-row-campane">
                        <button
                            className="fullscreen-btn-campane"
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {isFullscreen ? (
                                    <>
                                        <polyline points="4 14 10 14 10 20"></polyline>
                                        <polyline points="20 10 14 10 14 4"></polyline>
                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </>
                                ) : (
                                    <>
                                        <polyline points="15 3 21 3 21 9"></polyline>
                                        <polyline points="9 21 3 21 3 15"></polyline>
                                        <line x1="21" y1="3" x2="14" y2="10"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </>
                                )}
                            </svg>
                        </button>
                    </div>
                    <div className={`table-container-campane${isFullscreen ? ' fullscreen-table-campane' : ''}`}>
                        <RotateDevice active={isFullscreen} />
                        {isFullscreen && (
                            <div className="fullscreen-exit-bar-campane">
                                <button
                                    className="fullscreen-btn-campane"
                                    onClick={() => setIsFullscreen(false)}
                                    title="Esci da tutto schermo"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="4 14 10 14 10 20"></polyline>
                                        <polyline points="20 10 14 10 14 4"></polyline>
                                        <line x1="14" y1="10" x2="21" y2="3"></line>
                                        <line x1="3" y1="21" x2="10" y2="14"></line>
                                    </svg>
                                </button>
                            </div>
                        )}
                        <table className="data-table-campane">
                            <thead>
                                <tr>
                                    {[
                                        { label: 'Data', key: 'data' },
                                        { label: 'Comune', key: 'comune' },
                                        { label: 'Targa', key: 'targa' },
                                        { label: 'ODS', key: 'ods' },
                                        { label: 'Carico (kg)', key: 'carico_tot_kg' },
                                        { label: 'Gestito', key: 'gestito' },
                                        { label: 'Note', key: 'note' },
                                    ].map(({ label, key }) => (
                                        <th key={key} className="campane-th-sortable" onClick={() => handleSort(key)}>
                                            {label}
                                            <span className="campane-sort-icon">
                                                {sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                            </span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDati.map((row, idx) => (
                                    <tr key={idx}>
                                        <td>{new Date(row.data).toLocaleDateString('it-IT')}</td>
                                        <td>{row.comune || "-"}</td>
                                        <td>
                                            <span className="targa-badge-campane">{row.targa || "-"}</span>
                                        </td>
                                        <td>{row.ods || "-"}</td>
                                        <td className="kg-value-campane">{row.carico_tot_kg?.toFixed(0)}</td>
                                        <td>
                                            <label className="checkbox-wrapper-campane">
                                                <input
                                                    type="checkbox"
                                                    checked={row.gestito}
                                                    onChange={async () => {
                                                        const nuovoStato = !row.gestito;
                                                        try {
                                                            await axios.post(`${API_URL}/interacta/gestito`, {
                                                                data: row.data,
                                                                comune: row.comune,
                                                                targa: row.targa,
                                                                gestito: nuovoStato,
                                                                note: row.note
                                                            });

                                                            setDati(prev => {
                                                                const nuovo = [...prev];
                                                                nuovo[idx] = { ...nuovo[idx], gestito: nuovoStato };
                                                                return nuovo;
                                                            });
                                                            fetchStats();
                                                        } catch (err) {
                                                            console.error("Errore aggiornamento gestito:", err);
                                                        }
                                                    }}
                                                />
                                                <span className="checkmark-campane"></span>
                                            </label>
                                        </td>
                                        <td>
                                            <textarea
                                                defaultValue={row.note || ""}
                                                onBlur={async (e) => {
                                                    const nuovaNota = e.target.value;
                                                    if (nuovaNota === row.note) return;

                                                    try {
                                                        await axios.post(`${API_URL}/interacta/gestito`, {
                                                            data: row.data,
                                                            comune: row.comune,
                                                            targa: row.targa,
                                                            gestito: row.gestito,
                                                            note: nuovaNota
                                                        });

                                                        setDati(prev => {
                                                            const nuovo = [...prev];
                                                            nuovo[idx] = { ...nuovo[idx], note: nuovaNota };
                                                            return nuovo;
                                                        });
                                                    } catch (err) {
                                                        console.error("Errore aggiornamento nota:", err);
                                                    }
                                                }}
                                                className="note-input-campane"
                                                rows={2}
                                                placeholder="Aggiungi nota..."
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination (dentro table-container per fullscreen) */}
                        {!loading && filteredDati.length > 0 && (
                            <div className="pagination-compact">
                                <button
                                    className="pagination-btn-compact"
                                    disabled={page === 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15 18l-6-6 6-6"/>
                                    </svg>
                                    Prec
                                </button>
                                <div className="pagination-info-compact">
                                    <span className="pagination-text-compact">
                                        Pag <strong>{page}</strong> di <strong>{totalPages || 1}</strong>
                                    </span>
                                    <span className="pagination-total-compact">({total} risultati totali)</span>
                                </div>
                                <button
                                    className="pagination-btn-compact"
                                    disabled={page === totalPages || totalPages === 0}
                                    onClick={() => setPage(page + 1)}
                                >
                                    Succ
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 18l6-6-6-6"/>
                                    </svg>
                                </button>

                                <div className="items-per-page-compact">
                                    <span className="items-label-compact">Elementi per pagina:</span>
                                    <div className="items-buttons-compact">
                                        {[10, 20, 50, 100].map((num) => (
                                            <button
                                                key={num}
                                                className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                                onClick={() => {
                                                    setLimit(num);
                                                    setPage(1);
                                                }}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    </>
                ) : (
                    <div className="cards-grid-campane">
                        {filteredDati.map((row, idx) => (
                            <div key={idx} className="data-card-campane">
                                <div className="card-header-campane">
                                    <div className="card-date-campane">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        {new Date(row.data).toLocaleDateString('it-IT')}
                                    </div>
                                    <label className="checkbox-wrapper-campane">
                                        <input
                                            type="checkbox"
                                            checked={row.gestito}
                                            onChange={async () => {
                                                const nuovoStato = !row.gestito;
                                                try {
                                                    await axios.post(`${API_URL}/interacta/gestito`, {
                                                        data: row.data,
                                                        comune: row.comune,
                                                        targa: row.targa,
                                                        gestito: nuovoStato,
                                                        note: row.note
                                                    });

                                                    setDati(prev => {
                                                        const nuovo = [...prev];
                                                        nuovo[idx] = { ...nuovo[idx], gestito: nuovoStato };
                                                        return nuovo;
                                                    });
                                                    fetchStats();
                                                } catch (err) {
                                                    console.error("Errore aggiornamento gestito:", err);
                                                }
                                            }}
                                        />
                                        <span className="checkmark-campane"></span>
                                    </label>
                                </div>
                                <div className="card-body-campane">
                                    <div className="card-info-row-campane">
                                        <span className="card-label-campane">Comune</span>
                                        <span className="card-value-campane">{row.comune || "-"}</span>
                                    </div>
                                    <div className="card-info-row-campane">
                                        <span className="card-label-campane">Targa</span>
                                        <span className="targa-badge-campane">{row.targa || "-"}</span>
                                    </div>
                                    <div className="card-info-row-campane">
                                        <span className="card-label-campane">ODS</span>
                                        <span className="card-value-campane">{row.ods || "-"}</span>
                                    </div>
                                    <div className="card-kg-highlight-campane">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                                        </svg>
                                        <span className="kg-label-campane">Carico:</span>
                                        <span className="kg-value-card-campane">{row.carico_tot_kg?.toFixed(0)} kg</span>
                                    </div>
                                    <div className="card-notes-campane">
                                        <textarea
                                            defaultValue={row.note || ""}
                                            onBlur={async (e) => {
                                                const nuovaNota = e.target.value;
                                                if (nuovaNota === row.note) return;

                                                try {
                                                    await axios.post(`${API_URL}/interacta/gestito`, {
                                                        data: row.data,
                                                        comune: row.comune,
                                                        targa: row.targa,
                                                        gestito: row.gestito,
                                                        note: nuovaNota
                                                    });

                                                    setDati(prev => {
                                                        const nuovo = [...prev];
                                                        nuovo[idx] = { ...nuovo[idx], note: nuovaNota };
                                                        return nuovo;
                                                    });
                                                } catch (err) {
                                                    console.error("Errore aggiornamento nota:", err);
                                                }
                                            }}
                                            className="note-input-card-campane"
                                            rows={2}
                                            placeholder="Aggiungi nota..."
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
};

export default SvuotamentoCampane;
